import React, { useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import ReactDOM from 'react-dom';
import { useUIContext } from '../../contexts/UIContext';

const SidebarItem = ({ item, showDropdown, handleToggle, isSideBarExpand }) => {
    const location = useLocation();
    const { pathname } = location;
    const [showTooltip, setShowTooltip] = useState(false);
    const { closeMobileSidebar } = useUIContext();
    const itemRef = useRef(null);
    const tooltipRef = useRef(null);

    const isChildActive = item?.children?.some(child => pathname === child.route);
    const isDirectActive = pathname === item.route;
    const isParentActive = isDirectActive || isChildActive;

    const handleNavigate = () => {
        if (item?.children?.length > 0 && !isSideBarExpand) {
            handleToggle(item.id);
        }
    };

    const handleMouseEnter = () => {
        if (isSideBarExpand && item?.children?.length > 0) {
            setShowTooltip(true);
        }
    };

    const handleMouseLeave = () => {
        setTimeout(() => {
            if (!tooltipRef.current?.matches(':hover') && !itemRef.current?.matches(':hover')) {
                setShowTooltip(false);
            }
        }, 100);
    };

    const handleTooltipMouseLeave = () => {
        setTimeout(() => {
            if (!tooltipRef.current?.matches(':hover') && !itemRef.current?.matches(':hover')) {
                setShowTooltip(false);
            }
        }, 100);
    };

    const getLinkClass = () => {
        if (isSideBarExpand) {
            return isParentActive
                ? 'bg-primary text-white shadow-sm'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100';
        }

        if (item?.children?.length > 0) {
            return isChildActive
                ? 'bg-primary-soft/50 text-primary font-bold'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100/70 font-medium';
        }

        return isDirectActive
            ? 'bg-primary-soft text-primary font-bold shadow-xs'
            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100/70 font-medium';
    };

    return (
        <div
            key={item.id}
            title={isSideBarExpand ? item.title : ''}
            id={item.id}
            ref={itemRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            className="relative"
        >
            <Link
                to={!item?.route ? "#" : item.route}
                onClick={() => {
                    handleNavigate();
                    if (item.route && item.route !== "#") closeMobileSidebar();
                }}
                className={`flex items-center rounded-xl group relative transition-all text-xs md:text-sm ${
                    isSideBarExpand 
                        ? 'w-10 h-10 mx-auto justify-center p-0' 
                        : 'px-2.5 py-2'
                } ${getLinkClass()}`}
            >
                <div className={`flex-shrink-0 w-5 h-5 flex items-center justify-center transition-all ${
                    isSideBarExpand && isParentActive 
                        ? 'brightness-0 invert' 
                        : isParentActive 
                            ? 'text-primary' 
                            : 'text-slate-400 group-hover:text-slate-600'
                }`}>
                    {item.icon}
                </div>
                {!isSideBarExpand && (
                    <div className="flex-1 ml-3 whitespace-nowrap flex items-center justify-between">
                        <span className="truncate">{item.title}</span>
                        {item?.children?.length > 0 && (
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleToggle(item.id);
                                }}
                                className="p-0.5 rounded-md hover:bg-black/5 text-slate-400 group-hover:text-slate-600 transition-colors"
                                aria-label="Toggle submenu"
                            >
                                <ChevronRight
                                    className={`w-4 h-4 transition-transform duration-200 ${showDropdown === item.id ? 'rotate-90 text-primary' : ''}`}
                                />
                            </button>
                        )}
                    </div>
                )}
            </Link>

            {/* Expanded sidebar - show dropdown below */}
            {!isSideBarExpand && showDropdown === item.id && item.children && item.children.length > 0 && (
                <div className="ml-3 pl-2.5 my-1 space-y-0.5 border-l-2 border-slate-200/80">
                    {item.children.map(child => {
                        const isCurrent = pathname === child.route;
                        return (
                            <div key={child.id} title={child.title} id={child.id}>
                                <Link
                                    to={child.route}
                                    onClick={closeMobileSidebar}
                                    className={`flex items-center px-3 py-1.5 rounded-lg text-xs md:text-[13px] transition-all group ${
                                        isCurrent
                                            ? 'bg-primary-soft text-primary font-bold shadow-xs'
                                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100/70 font-medium'
                                    }`}
                                >
                                    <span className="truncate">{child.title}</span>
                                </Link>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Floating tooltip for collapsed sidebar */}
            {isSideBarExpand && showTooltip && item?.children?.length > 0 && (
                <>
                    {/* Invisible bridge between sidebar and tooltip - prevents accidental closing */}
                    <div
                        className="fixed z-40"
                        style={{
                            left: `${itemRef.current?.getBoundingClientRect().right}px`,
                            top: `${itemRef.current?.getBoundingClientRect().top}px`,
                            width: '16px',
                            height: `${itemRef.current?.getBoundingClientRect().height}px`,
                        }}
                    />
                    {ReactDOM.createPortal(
                        <div
                            ref={tooltipRef}
                            onMouseLeave={handleTooltipMouseLeave}
                            className="fixed bg-white border border-border rounded-xl shadow-xl py-2 min-w-[210px] z-50 text-xs md:text-sm animate-in fade-in zoom-in-95 duration-100"
                            style={{
                                left: `${itemRef.current?.getBoundingClientRect().right + 8}px`,
                                top: `${itemRef.current?.getBoundingClientRect().top}px`,
                            }}
                        >
                            <div className="px-3.5 py-1 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                                {item.title}
                            </div>
                            <div className="border-t border-slate-100 mt-1.5 pt-1.5 px-1.5 space-y-0.5">
                                {item.children.map(child => {
                                    const isCurrent = pathname === child.route;
                                    return (
                                        <Link
                                            key={child.id}
                                            to={child.route}
                                            className={`block px-3 py-1.5 rounded-lg text-xs md:text-[13px] transition-colors ${
                                                isCurrent 
                                                    ? 'bg-primary-soft text-primary font-bold' 
                                                    : 'text-slate-700 hover:bg-slate-100 font-medium'
                                            }`}
                                            onClick={() => setShowTooltip(false)}
                                        >
                                            {child.title}
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>,
                        document.body
                    )}
                </>
            )}
        </div>
    );
};

export default SidebarItem;
